<template>
  <FormContainer>
    <template #title>Dénomination du lieu de formation principal</template>
    <template #content>
      <div class="form__bloc">
        <WrapperToggleSwitch
          id="field_182"
          v-model="estIdentiqueCfaPrincipal"
          label="Le CFA responsable est-il le lieu de formation principal ?"
          :items="OuiOuNon"
          :required="meta.formation?.lieuFormationIdentiqueCfaPrincipal?.$status === PropertyStatus.Required"
          :alertLabel="meta.formation?.lieuFormationIdentiqueCfaPrincipal?.$messageErreurDirty!"
          :readonly
          class="col-10 col-md-14 col-sm-36"
        />
        <WrapperAutocomplete
          v-if="model?.formation?.lieuFormationIdentiqueCfaPrincipal === false"
          id="field_184"
          key-id="siret.valeur"
          key-label="denomination"
          v-model="cfaLieuFormationPrincipalModel"
          label="CFA de la prestation"
          hint="Siren ou Siret du CFA"
          class="col-26 col-md-22 col-sm-36"
          :search-min-length="9"
          :placeholder="PLACE_HOLDER_SIREN"
          :readonly
          strict
          :required="meta.formation?.cfaEtablissementLieuPrestation?.$status === PropertyStatus.Required"
          :alertLabel="meta.formation?.cfaEtablissementLieuPrestation?.$messageErreurDirty!"
          :search="searchPrestataireAll"
          :search-formater="sirenSiretSearchFormater"
        >
          <template v-slot="{ denomination, siret, adresse, estEtablissementSiege, etatAdministratif }">
            <div>
              <div>
                <BasicText weight="bold">{{ denomination }} - {{ siret.valeur }}</BasicText>
                <BasicText
                  v-if="estEtablissementSiege === true"
                  color="primary-600"
                >
                  - SIEGE
                </BasicText>
                <BasicText
                  v-if="etatAdministratif === 'F'"
                  color="red"
                >
                  (Inactif)
                </BasicText>
              </div>
              <div style="margin-top: -3px">
                <BasicText size="body-m">
                  {{ adresse?.voie }} {{ adresse?.codePostal }} {{ adresse?.commune }}
                </BasicText>
              </div>
            </div>
          </template>
        </WrapperAutocomplete>
        <WrapperInput
          v-else
          id="field_184"
          label="CFA de la prestation"
          hint="Siren ou Siret du CFA"
          class="col-26 col-md-22 col-sm-36"
          disabled
        />
      </div>
      <div class="form__bloc">
        <WrapperInput
          id="field_186"
          :model-value="model?.formation?.cfaEtablissementLieuPrestation?.siret?.valeur"
          label="SIRET"
          :alertLabel="meta.formation?.cfaEtablissementLieuPrestation?.siret?.valeur?.$messageErreurDirty!"
          class="col-8 col-md-12 col-sm-36"
          disabled
        />
        <WrapperInput
          id="field_185"
          :model-value="model?.formation?.cfaEtablissementLieuPrestation?.uai"
          label="N° UAI"
          :alertLabel="meta.formation?.cfaEtablissementLieuPrestation?.uai?.$messageErreurDirty!"
          class="col-4 col-md-8 col-sm-36"
          disabled
        />
        <WrapperInput
          id="field_187"
          :model-value="model?.formation?.cfaEtablissementLieuPrestation?.nda"
          label="N° d'activité"
          :alertLabel="meta.formation?.cfaEtablissementLieuPrestation?.nda?.$messageErreurDirty!"
          class="col-4 col-md-8 col-sm-36"
          disabled
        />
      </div>
      <div class="form__bloc">
        <WrapperInput
          id="field_189"
          :model-value="model?.formation?.cfaEtablissementLieuPrestation?.adresse?.voie"
          :label="LABEL_NUMERO_VOIE"
          :alertLabel="meta.formation?.cfaEtablissementLieuPrestation?.adresse?.complement?.$messageErreurDirty!"
          class="col-12 col-md-16 col-sm-36"
          disabled
        />
        <WrapperInput
          id="field_189"
          :model-value="model?.formation?.cfaEtablissementLieuPrestation?.adresse?.complement"
          label="Complément d'adresse"
          :alertLabel="meta.formation?.cfaEtablissementLieuPrestation?.adresse?.complement?.$messageErreurDirty!"
          class="col-12 col-md-16 col-sm-36"
          disabled
        />
        <WrapperInput
          id="field_190"
          :model-value="model?.formation?.cfaEtablissementLieuPrestation?.adresse?.codePostal"
          label="Code postal"
          :alertLabel="meta.formation?.cfaEtablissementLieuPrestation?.adresse?.codePostal?.$messageErreurDirty!"
          class="col-4 col-md-6 col-sm-36"
          disabled
        />
        <WrapperInput
          id="field_191"
          :model-value="model?.formation?.cfaEtablissementLieuPrestation?.adresse?.commune"
          label="Commune"
          :alertLabel="meta.formation?.cfaEtablissementLieuPrestation?.adresse?.commune?.$messageErreurDirty!"
          class="col-8 col-md-10 col-sm-36"
          disabled
        />
      </div>
    </template>
  </FormContainer>
</template>

<script setup lang="ts">
  import { inject } from 'vue'
  import { storeToRefs } from 'pinia'
  import { useDemandeCAPPStore } from '@/features/interface/demande/capp/useDemandeCAPPStore'
  import { PropertyStatus } from '@/features/domaine/property-status/PropertyStatus'
  import { OuiOuNon } from '@/features/interface/shared/tools'
  import { CAPP_ACTIONS } from '@/features/interface/demande/capp/useDemandeCAPPActions'
  import { FormContainer, LABEL_NUMERO_VOIE, PLACE_HOLDER_SIREN } from '@/features/interface/demande/shared'
  import type { EtablissementPrestataireDTO } from '@/features/api/extranet/dto/shared'
  import { sirenSiretSearchFormater } from '@/features/interface/shared/tools'

  const store = useDemandeCAPPStore()
  const { setupFieldWithKey } = store
  const { meta, model, readonly } = storeToRefs(store)
  const { searchPrestataireAll } = inject(CAPP_ACTIONS)!
  const estIdentiqueCfaPrincipal = setupFieldWithKey<boolean>('formation.lieuFormationIdentiqueCfaPrincipal')
  const cfaLieuFormationPrincipalModel = setupFieldWithKey<EtablissementPrestataireDTO>(
    'formation.cfaEtablissementLieuPrestation',
  )
</script>

